import CommonModal from 'components/common/CommonModal';
import ScreenLayout from 'components/common/ScreenLayout';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import DefaultText from 'components/common/DefaultText';
import Pagination from 'components/common/Pagination';
import moment from 'moment-timezone';
import {useNavigate} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import {userInfoState} from 'utils/Atoms';
import {
  changeTongPlaceHiddenState,
  searchBackOfficeTongPlace,
} from 'function/api';
import {convertTotalPage} from 'function/converter';
import {formatPhoneNumber} from 'utils/fomatPhoneNumber';
import {formatBusinessRegisterNumber} from 'function/formatter';
import BusinessImgModal from 'components/main/BusinessImgModal';
import {siteProcessingStatus} from 'data/status';
import ChangeStateModal from 'components/main/ChangeStateModal';
import {
  IHiddenState,
  ResponseSearchBackOfficeTongPlace,
} from 'interfaces/interface';
import LabelInput from 'components/common/LabelInput';
import RadioButton from 'components/common/RadioButton';
import ChangePaymentStateModal from 'components/main/ChangePaymentStateModal';
import ChangeEndDateStateModal from 'components/main/ChangeEndDateStateModal';
import ChangeTongPlaceNameModal from 'components/fieldManagement/ChangeTongPlaceNameModal';

/**
 * 현장 상태 관리 페이지 컴포넌트
 * 현장 목록을 조회하고 상태를 관리하는 페이지입니다.
 * @returns {JSX.Element} 현장 상태 관리 페이지 컴포넌트
 */
const SiteStateManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});

  const navigate = useNavigate();

  const userInfo = useRecoilValue(userInfoState);

  const [tongCurrentPage, setTongCurrentPage] = useState(1);
  const [tongTotalPage, setTongTotalPage] = useState(1);

  const [tongData, setTongData] =
    useState<null | ResponseSearchBackOfficeTongPlace>(null);

  const [modal, setModal] = useState('');

  const [selectItem, setSelectItem] = useState<null | any>(null);

  const [type, setType] = useState<IHiddenState>('ALL');

  const [keyword, setKeyword] = useState('');

  const modalData: {[key: string]: any} = {
    // 본사시스템 서비스 준비중
    servicePreparing: {
      title: 'servicePreparing',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
      },
    },
    changeBusinessRegisterNumberImg: {
      title: '사업자등록증 변경 성공',
      detail: '사업자등록증 변경이 완료되었습니다!',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
        window.location.reload();
      },
    },
    completeHide: {
      title: '현장 숨김 처리 완료',
      detail: '현장 숨김 처리가 완료되었습니다!',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
        window.location.reload();
      },
    },
    completeShow: {
      title: '현장 숨김 해제 완료',
      detail: '현장 숨김 해제가 완료되었습니다!',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
        window.location.reload();
      },
    },
  };

  useEffect(() => {
    if (!userInfo?.accessToken) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (userInfo?.accessToken) handleSearch();
  }, [userInfo, tongCurrentPage, type]);

  /**
   * 현장 검색을 수행합니다.
   * 키워드와 숨김 상태에 따라 현장 목록을 조회합니다.
   * @returns {Promise<void>}
   */
  const handleSearch = async () => {
    const result = await searchBackOfficeTongPlace({
      userInfo,
      setErrorModal,
      setIsLoading,
      page: tongCurrentPage,
      hiddenState: type,
      keyword,
    });
    if (result) {
      setTongData(result);

      const resTotalPage = convertTotalPage(result.totalCount ?? 0, 10);
      setTongTotalPage(resTotalPage);
    }
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <Inner>
          {/* 현장 리스트 - 사용X / 임시 복원 */}
          <DefaultText
            text="tongPlace"
            textStyle={{
              fontSize: minmax(30),
              fontWeight: 'bold',
              borderBottom: `3px solid ${colors.primary}`,
              width: 'fit-content',
              marginBottom: minmax(20),
            }}
          />

          <SearchContainer>
            {(['ALL', 'HIDDEN', 'VISIBLE'] as IHiddenState[]).map(item => (
              <RadioButton
                key={item}
                checked={item === type}
                containerStyle={{alignItems: 'center', columnGap: minmax(10)}}
                onChange={() => setType(item)}>
                <DefaultText text={item} />
              </RadioButton>
            ))}

            <Border />

            <LabelInput
              type={'text'}
              labelText={''}
              value={keyword}
              labelInputContainerStyle={{flex: 1}}
              labelStyle={{display: 'none'}}
              rightImg="images/common/search.png"
              placeholder="searchPlaceholder"
              onClickRightImg={handleSearch}
              setValue={(e: any) => setKeyword(e)}
              inputStyle={{paddingRight: minmax(70)}}
              onKeyPress={e => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
          </SearchContainer>

          <ResultArea>
            <ResultHeader>
              <DefaultText
                text=""
                textStyle={{
                  ...ResultHeaderTextStyle,
                  flex: 0.4,
                }}
              />
              <DefaultText
                text="registerDate"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText text="applicant" textStyle={ResultHeaderTextStyle} />
              <DefaultText
                text="applicantNumber"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText
                text="siteName"
                textStyle={{...ResultHeaderTextStyle, flex: 2}}
              />
              <DefaultText
                text="siteAddress"
                textStyle={{...ResultHeaderTextStyle, flex: 2}}
              />
              <DefaultText
                text="businessLicense"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText
                text="businessRegistrationNumber"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText
                text="DUNS"
                textStyle={{...ResultHeaderTextStyle, flex: 1}}
              />
              <DefaultText
                text="constructionPeriod"
                textStyle={{...ResultHeaderTextStyle, flex: 2}}
              />

              <DefaultText text="state" textStyle={ResultHeaderTextStyle} />
              <DefaultText
                text="paymentStatus"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText text="hide" textStyle={ResultHeaderTextStyle} />
            </ResultHeader>

            <ResultList>
              {tongData?.totalCount ? (
                tongData.tongPlaceList.map((item, index: number) => (
                  <ResultItem
                    key={item.uuid}
                    onClick={() => {
                      //
                    }}>
                    <DefaultText
                      text={((tongCurrentPage - 1) * 10 + index + 1).toString()}
                      textStyle={{...ResultHeaderTextStyle, flex: 0.4}}
                    />
                    <DefaultText
                      text={moment(item.createdAt).format('YY-MM-DD')}
                      textStyle={ResultItemStyle}
                    />
                    <DefaultText
                      text={item.owner.profile?.name || '-'}
                      textStyle={ResultItemStyle}
                    />
                    <DefaultText
                      text={formatPhoneNumber({
                        phone: item.owner.phone,
                        locale: item.owner.locale,
                      })}
                      textStyle={{...ResultItemStyle, flex: 1.5}}
                    />
                    <DefaultText
                      text={item.name}
                      textStyle={{
                        ...ResultItemStyle,
                        flex: 2,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                      onClick={() =>
                        setSelectItem({...item, type: 'changeName'})
                      }
                    />
                    <DefaultText
                      text={
                        item.address +
                        (item.addressDetail ? ', ' + item.addressDetail : '')
                      }
                      textStyle={{...ResultItemStyle, flex: 2}}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                      }}>
                      {item.businessRegisterNumberImg ? (
                        <DefaultText
                          text={'확인하기'}
                          textStyle={{
                            width: 'fit-content',
                            cursor: 'pointer',
                            backgroundColor: colors.primary2,
                            color: colors.white,
                            borderRadius: minmax(10),
                            padding: minmax(10),
                          }}
                          onClick={() =>
                            setSelectItem({...item, type: 'business'})
                          }
                        />
                      ) : (
                        '-'
                      )}
                    </div>
                    <DefaultText
                      text={formatBusinessRegisterNumber(
                        item.businessRegisterNumber,
                      )}
                      textStyle={{...ResultItemStyle, flex: 1.5}}
                    />
                    <DefaultText
                      text={item.dunsNumber || '-'}
                      textStyle={{...ResultItemStyle, flex: 1}}
                    />
                    <DefaultText
                      text={
                        `${moment(item.constructionStartDate).format(
                          'YY-MM-DD',
                        )}~${moment(item.constructionEndDate).format(
                          'YY-MM-DD',
                        )}` || '-'
                      }
                      textStyle={{
                        ...ResultItemStyle,
                        flex: 2,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSelectItem({
                          ...item,
                          type: 'date',
                        })
                      }
                    />

                    <DefaultText
                      text={siteProcessingStatus[item.state].text}
                      textStyle={{
                        ...ResultItemStyle,
                        color: siteProcessingStatus[item.state].color,
                      }}
                      // onClick={() =>
                      // setSelectItem({
                      //   ...item,
                      //   type: 'state',
                      // })
                      // }
                    />
                    <DefaultText
                      text={
                        item?.tongPlaceSignState?.isApproved
                          ? 'successPayment'
                          : 'outstanding'
                      }
                      textStyle={{
                        ...ResultItemStyle,
                        flex: 1,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSelectItem({
                          ...item,
                          type: 'payment',
                        })
                      }
                    />
                    {/* TODO: 숨김 */}
                    <p
                      style={{
                        ...ResultItemStyle,
                        color: siteProcessingStatus[item.state].color,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSelectItem({
                          ...item,
                          type: 'hide',
                        })
                      }>
                      {(item as any).deletedAt ? 'O' : 'X'}
                    </p>
                  </ResultItem>
                ))
              ) : (
                <DefaultText
                  text="noDataMsgTongList"
                  textStyle={{
                    color: colors.darkGray,
                    textAlign: 'center',
                    margin: `${minmax(12)} 0`,
                  }}
                />
              )}
            </ResultList>
          </ResultArea>

          {tongData?.totalCount ? (
            <Pagination
              totalPage={tongTotalPage}
              currentPage={tongCurrentPage}
              setCurrentPage={setTongCurrentPage}
            />
          ) : null}
        </Inner>
      </Container>

      {modal !== '' && (
        <CommonModal
          title={modalData[modal].title}
          detail={modalData[modal].detail}
          firstButtonText={modalData[modal].firstButtonText}
          firstButtonStyle={modalData[modal].firstButtonStyle}
          secondButtonText={modalData[modal].secondButtonText}
          secondButtonStyle={modalData[modal].secondButtonStyle}
          firstButtonClick={modalData[modal].firstButtonClick}
          secondButtonClick={modalData[modal].secondButtonClick}
          closeModal={modalData[modal].firstButtonClick}
        />
      )}

      {/* 상태 변경 | 사업자등록증 모달 */}
      {selectItem &&
        (selectItem.type === 'state' ? (
          <ChangeStateModal
            selectItem={selectItem}
            closeModal={() => setSelectItem(null)}
            setIsLoading={setIsLoading}
            setErrorModal={setErrorModal}
          />
        ) : selectItem.type === 'business' ? (
          <BusinessImgModal
            selectItem={selectItem}
            closeModal={() => setSelectItem(null)}
            setIsLoading={setIsLoading}
            setErrorModal={setErrorModal}
          />
        ) : selectItem.type === 'payment' ? (
          <ChangePaymentStateModal
            selectItem={selectItem}
            closeModal={() => setSelectItem(null)}
            setIsLoading={setIsLoading}
            setErrorModal={setErrorModal}
          />
        ) : selectItem.type === 'date' ? (
          <ChangeEndDateStateModal
            selectItem={selectItem}
            closeModal={() => setSelectItem(null)}
            setIsLoading={setIsLoading}
            setErrorModal={setErrorModal}
          />
        ) : selectItem.type === 'changeName' ? (
          <ChangeTongPlaceNameModal
            selectItem={selectItem}
            closeModal={() => setSelectItem(null)}
            setIsLoading={setIsLoading}
            setErrorModal={setErrorModal}
          />
        ) : (
          <CommonModal
            title={`해당 현장을 숨김 ${
              selectItem.deletedAt ? '해제' : '처리'
            }하시겠습니까?`}
            detail={
              '현장명: ' +
              selectItem.name +
              (selectItem.deletedAt
                ? '\n숨김을 해제하면 앱과 PC관리자 조회에 다시 나타납니다.'
                : '\n숨김 처리하면 앱의 현장 찾아 출근신청과 PC관리자 조회에서 빠집니다.')
            }
            firstButtonText="cancel"
            firstButtonStyle={{backgroundColor: colors.darkGray}}
            firstButtonClick={() => setSelectItem(null)}
            secondButtonStyle={{backgroundColor: colors.primary2}}
            secondButtonText="confirm"
            secondButtonClick={async () => {
              const wasHidden = !!selectItem.deletedAt;
              const result = await changeTongPlaceHiddenState({
                userInfo,
                setIsLoading,
                setErrorModal,
                tongPlaceUUID: selectItem.uuid,
                hiddenState: wasHidden ? 'VISIBLE' : 'HIDDEN',
              });
              setSelectItem(null);
              // 실패하면 에러 모달이 이미 떠 있으므로 완료 안내를 띄우지 않는다.
              if (!result) return;
              await handleSearch();
              setModal(wasHidden ? 'completeShow' : 'completeHide');
            }}
          />
        ))}
    </ScreenLayout>
  );
};

export default SiteStateManagement;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;

const Inner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${minmax(20)};
`;

const SearchContainer = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: ${minmax(20)};
  column-gap: ${minmax(20)};
`;

const Border = styled.div`
  width: 1px;
  height: ${minmax(60)};
  background-color: ${colors.borderGray};
`;

const ResultArea = styled.div`
  margin-bottom: ${minmax(40)};
  border-bottom: 1px solid ${colors.darkGray};
`;

const ResultHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${minmax(52)};
  background-color: #f7f7f7;
`;

const ResultList = styled.ul`
  /* height: 728px; */
  /* overflow-y: auto; */

  // 스크롤바 삭제
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const ResultItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${minmax(8)} 0;
  min-height: ${minmax(52)};
  border-top: 1px solid ${colors.lightGrey};
  &:last-child {
    border-bottom: none;
  }
`;

const ResultHeaderTextStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  color: '#585858',
};

const ResultItemStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  fontSize: minmax(20),
  // overflow: 'hidden',
  // textOverflow: 'ellipsis',
  // whiteSpace: 'nowrap',
  // wordBreak: 'break-all',
};
